import "./ActiveTrips.scss";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import JobService from "../../services/JobService";
import { useLoader } from "../../contexts/LoaderContext";
import { Trip, TripStats } from "../../interfaces/trip.interface";
import SearchBar from "../../components/SearchBar/SearchBar";
import TableComponent from "../../components/TableComponent/TableComponent";
import {
  containerSizeStringToNumber,
  dateFromNow,
  formatIsoDate,
  transformTruckNumber,
} from "../../utils/general";
import StatsBoxContainer from "../../components/StatsBoxContainer/StatsBoxContainer";
import Modal from "../../components/Modal/Modal";
import LiveTracking from "../../components/LiveTracking/LiveTracking";
import { JobBreakUpDetail, JobOrder } from "../../interfaces/job.interface";
import JobDetails from "../../components/Jobs/JobDetails";
import TransporterDetails from "../../components/TransporterDetails";
import ContainerDetails from "../../components/Jobs/ContainerDetails";
import MapComponent from "../../components/MapComponent/MapComponent";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter-icon.svg";
import { useLoading } from "../../contexts/LoadingContext";
import ContainerType from "../../components/ContainerType/ContainerType";
import ContainerStatus from "../../components/ContainerStatus/ContainerStatus";
import UpdateContainerModal from "../../components/Jobs/UpdateContainerModal";
import UpdateTruckModal from "../../components/Jobs/UpdateTruckModal";
import ForceEndTripModal from "../../components/Jobs/ForceEndTripModal";
import { showToast } from "../../components/Toaster";
import TripService from "../../services/TripService";
import TransferTripModal from "../../components/Jobs/TransferTripModal";

// TruckNumberCell component to handle both click functionality and search highlighting
const TruckNumberCell = ({
  value,
  row,
  onClick,
  searchQuery,
}: {
  value: string;
  row: Record<string, any>;
  onClick: (row: Record<string, any>) => void;
  searchQuery?: string;
}) => {
  if (!value || value === "N/A") return <span className="na-value">N/A</span>;

  // Highlight text if search query exists
  const highlightText = (text: string, query: string): JSX.Element[] => {
    if (!query || !text) return [<span key="text">{text || "N/A"}</span>];

    const regex = new RegExp(`(${query})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        return (
          <span key={`highlight-${index}`} className="highlight">
            {part}
          </span>
        );
      }
      return <span key={`text-${index}`}>{part}</span>;
    });
  };

  const isTrackable =
    row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED";

  if (isTrackable) {
    return (
      <span
        className="text-[#027CB5] hover:underline cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClick(row);
        }}
      >
        {searchQuery ? highlightText(value, searchQuery) : value}
      </span>
    );
  }

  return <span>{searchQuery ? highlightText(value, searchQuery) : value}</span>;
};

function ActiveTrips() {
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobOrder | null>(null);
  const [selectedJobBreakupDetailId, setSelectedJobBreakupDetailId] = useState<
    number | null
  >(null);
  const [isJobDetailsModalOpen, setIsJobDetailsModalOpen] = useState(false);
  const [jobDetailsView, setJobDetailsView] = useState("jobDetails");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(0);
  const REFRESH_COOLDOWN = 2000; // 2 seconds cooldown between refreshes
  const [tripsLoaded, setTripsLoaded] = useState(false);
  const initialLoadDone = useRef(false);
  const isLoading = useRef(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editContainerData, setEditContainerData] = useState<{
    jobBreakupDetailId: number;
    containerNumber: string;
    containerSize: number;
    containerType: string;
    driverIdName: string;
    truckIdName: string;
    transporterIdName: string;
    jobCode: string;
    source: string;
    destination: string;
  } | null>(null);
  const [isTruckModalOpen, setIsTruckModalOpen] = useState(false);
  const [editTruckData, setEditTruckData] = useState<{
    jobBreakupDetailId: number;
    currentTruckNumber: string;
    driverId: number;
    transporterId: number;
    driverName: string;
    transporterName: string;
  } | null>(null);
  const [tripStats, setTripStats] = useState<TripStats>({
    totalActiveTrips: 0,
    totalContainers: 0,
    total20: 0,
    total40: 0,
    total45: 0,
  });
  const [isForceEndTripModalOpen, setIsForceEndTripModalOpen] = useState(false);
  const [selectedTripToEnd, setSelectedTripToEnd] = useState<{
    jobBreakupDetailId: number;
    jobCode: string;
    containerNumber: string;
    containerSiTy: string;
    truckIdName: string;
    driverIdName: string;
    transporterIdName: string;
    pickedAt: string;
    tripStatus: string;
    source: string;
    destination: string;
  } | null>(null);
  const [isTransferTripModalOpen, setIsTransferTripModalOpen] = useState(false);
  const [transferTripData, setTransferTripData] = useState<{
    driverId: number;
    driverName: string;
    truck: string;
    tripId: number;
    transporterId?: number;
    transporterName?: string;
  } | null>(null);

  const handleEditContainer = (row: Record<string, any>) => {
    console.log("Row:", row);

    // Extract container size and type from containerSiTy
    const [size, type] = row.containerSiTy.split(" ");

    setEditContainerData({
      jobBreakupDetailId: row.jobBreakupDetailId,
      containerNumber: row.containerNumber || "",
      containerSize: parseInt(size) || 0,
      containerType: type || "",
      driverIdName: row.driverIdName || "N/A",
      truckIdName: row.truckIdName || "N/A",
      transporterIdName: row.transporterIdName || "N/A",
      jobCode: row.jobCode || "",
      source: row.source || "",
      destination: row.destination || "",
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditContainerData(null);
  };

  const handleEditTruck = (row: Record<string, any>) => {
    console.log("Edit Truck Row:", row);

    // Find the trip to get driver information
    const trip = trips.find(
      (trip) => trip.jobBreakupDetailId === row.jobBreakupDetailId
    );

    if (!trip) {
      showToast("Trip not found", "error");
      return;
    }

    // Check if truck number is available
    if (!trip.truckIdName?.name) {
      showToast("No truck assigned to this trip", "error");
      return;
    }

    setEditTruckData({
      jobBreakupDetailId: row.jobBreakupDetailId,
      currentTruckNumber: trip.truckIdName.name,
      driverId: trip.driverIdName.id,
      transporterId: trip.transporterIdName?.id || 0,
      driverName: trip.driverIdName?.name || "N/A",
      transporterName: trip.transporterIdName?.name || "N/A",
    });
    setIsTruckModalOpen(true);
  };

  const handleCloseTruckModal = () => {
    setIsTruckModalOpen(false);
    setEditTruckData(null);
  };

  const handleUpdateContainer = async () => {
    if (!editContainerData) return;

    try {
      loader.setLoading(true);
      const tripContainer = {
        tripId: editContainerData.jobBreakupDetailId,
        containerNumber: editContainerData.containerNumber,
      };

      const response = await JobService.assignContainers([tripContainer]);
      if (response.data.success) {
        showToast("Container updated successfully", "success");
        handleCloseEditModal();
        await fetchActiveTrips(true, true);
      } else {
        showToast(
          response.data.message || "Failed to update container",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating container:", error);
      showToast("Failed to update container", "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const fetchActiveTrips = useCallback(
    async (suppressLoader?: boolean, forceRefresh?: boolean) => {
      const now = Date.now();
      if (!forceRefresh && now - lastRefreshTime.current < REFRESH_COOLDOWN) {
        console.log("Refresh skipped - too soon since last refresh");
        if (!suppressLoader) {
          hideLoader();
        } else {
          loader.setLoading(false);
        }
        return;
      }
      lastRefreshTime.current = now;

      try {
        if (!initialLoadDone.current) {
          setTripsLoaded(false);
        }

        if (!suppressLoader) {
          showLoader();
        } else {
          loader.setLoading(true);
        }

        setError(null);

        // Fetch both trips and stats in parallel
        const [tripsResponse, statsResponse] = await Promise.all([
          JobService.getActiveTrips(),
          JobService.getActiveTripStats(),
        ]);

        if (!tripsResponse.data.error) {
          const trips: Trip[] = tripsResponse.data.data.content;
          setTrips(trips);
        } else {
          console.error("Error in fetching trips:", tripsResponse.data.message);
          setError(tripsResponse.data.message || "Error fetching trips");
        }

        if (!statsResponse.data.error) {
          // Map the API response to our TripStats interface
          setTripStats({
            totalActiveTrips: statsResponse.data.data.trips,
            totalContainers: statsResponse.data.data.containers,
            total20: statsResponse.data.data.count20,
            total40: statsResponse.data.data.count40,
            total45: statsResponse.data.data.count45,
          });
        } else {
          console.error(
            "Error in fetching trip stats:",
            statsResponse.data.message
          );
          // Don't set error state here as it's not critical
        }
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setError(err.message || "An error occurred while fetching data");
      } finally {
        setTripsLoaded(true);
        initialLoadDone.current = true;

        setTimeout(() => {
          if (!suppressLoader) {
            hideLoader();
          } else {
            loader.setLoading(false);
          }
        }, 1000);
      }
    },
    [showLoader, hideLoader, loader]
  );

  // Memoize table data transformation
  const tableData = useMemo(() => {
    console.log("Raw trips data:", trips);
    return trips.map((trip) => {
      console.log("Processing trip:", trip);
      console.log("truckIdName:", trip.truckIdName);
      const transformedData = {
        jobCode: trip.jobDetails.jobCode,
        containerNumber: trip.containerNumber,
        containerSiTy:
          containerSizeStringToNumber(trip.containerSize) +
          " " +
          trip.containerType,
        truckIdName: trip.truckIdName?.name
          ? transformTruckNumber(trip.truckIdName.name)
          : "N/A",
        driverIdName: trip.driverIdName.name || "N/A",
        transporterIdName: trip.transporterIdName.name || "N/A",
        pickedAt: trip.startDate ? formatIsoDate(trip.startDate) : "N/A",
        status: trip.tripStatus || "In Transit",
        jobBreakupDetailId: trip.jobBreakupDetailId,
        updatedAt: trip.updatedDate ? dateFromNow(trip.updatedDate) : "N/A",
        tripStatus: trip.tripStatus,
        source: trip.jobDetails.source,
        destination: trip.jobDetails.destination,
        jobId: trip.jobDetails.jobId,
      };
      console.log("Transformed data:", transformedData);
      return transformedData;
    });
  }, [trips]);

  // Memoize search suggestions
  const searchSuggestionsMemo = useMemo(() => {
    if (tableData.length > 0) {
      // Extract unique values from relevant columns for suggestions
      const suggestions = new Set<string>();

      tableData.forEach((row) => {
        // Add container numbers
        if (row.containerNumber) {
          suggestions.add(row.containerNumber);
        }

        // Add truck numbers
        if (row.truckIdName) {
          suggestions.add(row.truckIdName);
        }

        // Add transporter names
        if (row.transporterIdName) {
          suggestions.add(row.transporterIdName);
        }

        // Add driver names
        if (row.driverIdName) {
          suggestions.add(row.driverIdName);
        }
      });

      return Array.from(suggestions);
    }
    return [];
  }, [tableData]);

  // Memoize callback functions
  const handleJobCodeClick = useCallback(
    async (row: Record<string, any>) => {
      try {
        loader.setLoading(true);
        const trip = trips.find(
          (trip) => trip.jobBreakupDetailId === row.jobBreakupDetailId
        );
        if (trip) {
          const response = await JobService.getJobDetails(
            trip.jobDetails.jobId
          );
          if (!response.data.error) {
            setSelectedJob(response.data.data);
            setSelectedJobBreakupDetailId(row.jobBreakupDetailId);
            setIsJobDetailsModalOpen(true);
          }
        }
      } catch (err) {
        console.error("Error fetching job details:", err);
      } finally {
        loader.setLoading(false);
      }
    },
    [trips, loader]
  );

  const handleTruckNumberClick = useCallback(
    async (row: Record<string, any>) => {
      try {
        // Don't show loader here since LiveTracking has its own loading state
        const trip = trips.find(
          (trip) => trip.jobBreakupDetailId === row.jobBreakupDetailId
        );
        if (trip) {
          const response = await JobService.getJobDetails(
            trip.jobDetails.jobId
          );
          if (!response.data.error) {
            setSelectedJob(response.data.data);
            setSelectedJobBreakupDetailId(row.jobBreakupDetailId);
            setIsTrackingModalOpen(true);
          }
        }
      } catch (err) {
        console.error("Error fetching job details:", err);
      }
    },
    [trips]
  );

  const handleRowClick = useCallback((row: Record<string, any>) => {
    // console.log("Row clicked:", row);
    // handleTrackClick(row);
  }, []);

  const handleSelectionChange = useCallback((selectedRows: number[]) => {
    console.log("Selected row indices:", selectedRows);
  }, []);

  const handleForceEndTrip = useCallback((row: Record<string, any>) => {
    setSelectedTripToEnd({
      jobBreakupDetailId: row.jobBreakupDetailId,
      jobCode: row.jobCode,
      containerNumber: row.containerNumber,
      containerSiTy: row.containerSiTy,
      truckIdName: row.truckIdName,
      driverIdName: row.driverIdName,
      transporterIdName: row.transporterIdName,
      pickedAt: row.pickedAt,
      tripStatus: row.tripStatus,
      source: row.source,
      destination: row.destination,
    });
    setIsForceEndTripModalOpen(true);
  }, []);

  const handleCloseForceEndTripModal = useCallback(() => {
    setIsForceEndTripModalOpen(false);
    setSelectedTripToEnd(null);
  }, []);

  const handleEndTripSuccess = useCallback(async () => {
    await fetchActiveTrips(true, true);
  }, [fetchActiveTrips]);

  // Add handler to open transfer modal
  const handleOpenTransferTripModal = (row: Record<string, any>) => {
    // Find the original trip data to get the proper driver and transporter information
    const originalTrip = trips.find(
      (trip) => trip.jobBreakupDetailId === row.jobBreakupDetailId
    );

    if (!originalTrip) {
      showToast("Trip not found", "error");
      return;
    }

    setTransferTripData({
      driverId: originalTrip.driverIdName?.id,
      driverName: originalTrip.driverIdName?.name || "Unknown",
      truck: originalTrip.truckIdName?.name || "N/A",
      tripId: originalTrip.jobBreakupDetailId,
      transporterId: originalTrip.transporterIdName?.id,
      transporterName: originalTrip.transporterIdName?.name,
    });
    setIsTransferTripModalOpen(true);
  };

  // Memoize columns definition
  const columns = useMemo(
    () => [
      {
        header: "Job Code",
        accessor: "jobCode",
        sortable: true,
        cell: (value: any, row: any) => {
          console.log("Row:", row);
          return (
            <span
              className="text-[#027CB5] hover:underline cursor-pointer"
              onClick={() => navigate(`/jobs/all/${row.jobId}/${row.jobCode}`)}
            >
              {value}
            </span>
          );
        },
      },
      {
        header: "Trip Id.",
        accessor: "jobBreakupDetailId",
        sortable: true,
        centerAlign: true,
        width: "100px",
        cell: (value: any, row: any) => {
          const isTrackable =
            row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED";
          const content = isTrackable ? (
            <span
              className="text-[#027CB5] hover:underline cursor-pointer"
              onClick={() =>
                navigate(
                  `/jobs/all/${row.jobId}/${row.jobCode}/containers/${row.jobBreakupDetailId}/live-tracking`
                )
              }
            >
              {value}
            </span>
          ) : (
            <span>{value}</span>
          );
          return content;
        },
      },
      {
        header: "Container No.",
        accessor: "containerNumber",
        uppercase: true,
        width: "130px",
        minWidth: "130px",
      },
      {
        header: "Size & Type",
        accessor: "containerSiTy",
        sortable: true,
        centerAlign: true,
        width: "120px",
        minWidth: "120px",
        cell: (value: string) => {
          const [size, type] = value.split(" ");
          return <ContainerType size={size} type={type} />;
        },
      },
      {
        header: "Truck Number",
        accessor: "truckIdName",
        width: "140px",
        minWidth: "140px",
        cell: (value: string, row: Record<string, any>) => {
          return (
            <TruckNumberCell
              value={value}
              row={row}
              onClick={handleTruckNumberClick}
              searchQuery={searchQuery}
            />
          );
        },
      },
      {
        header: "Driver Name",
        accessor: "driverIdName",
      },
      {
        header: "Transporter Name",
        accessor: "transporterIdName",
        sortable: true,
        width: "150px",
        minWidth: "150px",
      },
      {
        header: "Picked at",
        accessor: "pickedAt",
        sortable: true,
        // width: "150px",
        // minWidth: "150px",
      },
      {
        header: "Last Updated",
        accessor: "updatedAt",
        sortable: true,
        centerAlign: true,
      },
      {
        header: "Status",
        accessor: "tripStatus",
        sortable: true,
        centerAlign: true,
        width: "120px",
        cell: (value: string, row: Record<string, any>) => {
          return <ContainerStatus container={row as JobBreakUpDetail} />;
        },
      },
    ],
    [handleTruckNumberClick, searchQuery, navigate]
  );

  // Memoize table actions
  const tableActions = useMemo(
    () => [
      {
        label: "Update Container Number",
        callback: handleEditContainer,
        isEnabled: (row: Record<string, any>) => true,
      },
      // {
      //   label: "Update Truck Number",
      //   callback: handleEditTruck,
      //   isEnabled: (row: Record<string, any>) => {
      //     // Find the original trip data to check if truck is assigned
      //     const trip = trips.find(
      //       (trip) => trip.jobBreakupDetailId === row.jobBreakupDetailId
      //     );
      //     return !!(trip?.truckIdName?.name && trip.truckIdName.name !== "N/A");
      //   },
      // },
      // {
      //   label: "Transfer Trip",
      //   callback: handleOpenTransferTripModal,
      //   isEnabled: (row: Record<string, any>) =>
      //     row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED",
      // },

      {
        label: "Force End Trip",
        callback: handleForceEndTrip,
        isEnabled: (row: Record<string, any>) =>
          row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED",
      },
      {
        label: "Live Tracking",
        callback: handleTruckNumberClick,
        isEnabled: (row: Record<string, any>) =>
          (row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED") &&
          row?.truckIdName !== "N/A",
      },
      // {
      //   label: "View Images",
      //   callback: () => {
      //     console.log("View Images");
      //   },
      //   isEnabled: (row: Record<string, any>) => false,
      // },
      // {
      //   label: "View Documents",
      //   callback: () => {
      //     console.log("View Documents");
      //   },
      //   isEnabled: (row: Record<string, any>) => false,
      // },
    ],
    [
      handleTruckNumberClick,
      handleEditContainer,
      handleEditTruck,
      handleForceEndTrip,
      trips,
    ]
  );

  // Memoize stats data
  const statsData = useMemo(
    () => [
      {
        name: "Total Active Trips",
        value: tripStats.totalActiveTrips,
        valueColor: "#59585B",
      },
      {
        name: "20ft",
        value: tripStats.total20,
        valueColor: "#1D3557",
      },
      {
        name: "40ft",
        value: tripStats.total40,
        valueColor: "#457B9D",
      },
      {
        name: "45ft",
        value: tripStats.total45,
        valueColor: "#69B9BC",
      },
    ],
    [tripStats]
  );

  // Initial data fetch and auto-refresh setup
  useEffect(() => {
    // Fetch data
    const fetchData = async () => {
      try {
        await fetchActiveTrips(false);
      } finally {
        // No need to hide loader here as it's handled in fetchActiveTrips
      }
    };

    fetchData();

    // Set up auto-refresh if enabled
    if (autoRefreshEnabled) {
      autoRefreshInterval.current = setInterval(() => {
        fetchActiveTrips(true);
      }, 30000); // Refresh every 30 seconds
    }

    // Clean up interval on component unmount
    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [fetchActiveTrips, autoRefreshEnabled]);

  // Update search suggestions when table data changes
  useEffect(() => {
    setSearchSuggestions(searchSuggestionsMemo);
  }, [searchSuggestionsMemo]);

  // Toggle auto-refresh
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled((prev) => !prev);
  }, []);

  // Handle manual refresh
  const handleManualRefresh = useCallback(() => {
    // Don't set loading state here, let fetchActiveTrips handle it
    fetchActiveTrips(true, true); // Use forceRefresh=true to bypass cooldown
  }, [fetchActiveTrips]);

  // Handle search input changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <div className="active-trips-page p-[20px]">
      <StatsBoxContainer stats={statsData}></StatsBoxContainer>

      <div className="flex justify-between items-center py-[30px]">
        <SearchBar
          placeholder="Search"
          onSearch={handleSearchChange}
          showClearButton={true}
          suggestions={searchSuggestions}
          onSuggestionClick={(suggestion) => {
            console.log("Selected suggestion:", suggestion);
            setSearchQuery(suggestion);
          }}
        />
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className={`secondary_button ${
                loader.isLoading ? "opacity-70" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={loader.isLoading}
            >
              <div className="flex items-center">
                {loader.isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  "Refresh"
                )}
              </div>
            </button>
            <button
              type="button"
              className={`secondary_button ${
                autoRefreshEnabled ? "active" : ""
              }`}
              onClick={toggleAutoRefresh}
              title={
                autoRefreshEnabled
                  ? "Auto-refresh enabled (every 30s)"
                  : "Auto-refresh disabled"
              }
            >
              <div className="flex items-center">
                {autoRefreshEnabled ? "Auto-refresh On" : "Auto-refresh Off"}
              </div>
            </button>
          </div>
        </div>
      </div>

      {tripsLoaded && !loader.isLoading && tableData.length === 0 ? (
        <div className="flex justify-center items-center">
          <p className="text-gray-500">No active trips found</p>
        </div>
      ) : (
        <div className="table">
          <TableComponent
            showPagination={false}
            columns={columns}
            data={tableData}
            onRowClick={handleRowClick}
            onSelectionChange={handleSelectionChange}
            searchQuery={searchQuery}
            showCheckboxes={false}
            uniqueAccessor="jobBreakupDetailId"
            actions={tableActions}
          ></TableComponent>
        </div>
      )}

      {selectedJob && selectedJobBreakupDetailId && (
        <Modal
          size="full"
          className="live-tracking-modal"
          title="Live Tracking"
          isOpen={isTrackingModalOpen}
          onClose={() => {
            setIsTrackingModalOpen(false);
            // Don't clear selectedJob and selectedJobBreakupDetailId here
            // to prevent unnecessary refreshes
          }}
        >
          <LiveTracking
            job={selectedJob}
            jobBreakupDetailId={selectedJobBreakupDetailId}
          />
        </Modal>
      )}
      {selectedJob && (
        <Modal
          className="job_preview_modal"
          title={`#${selectedJob.jobCode}`}
          isOpen={isJobDetailsModalOpen}
          onClose={() => {
            setIsJobDetailsModalOpen(false);
            setSelectedJob(null);
            setSelectedJobBreakupDetailId(null);
          }}
        >
          <div className="bg-card rounded-[10px] selected-job-details card-shadow w-full h-full flex flex-col">
            <div className="flex px-[20px] border-b border-bottomline justify-between">
              <div className="flex">
                <button
                  className={`mr-2 py-2 px-4 font-w-[500] ${
                    jobDetailsView === "jobDetails"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => setJobDetailsView("jobDetails")}
                >
                  Job Details
                </button>
                <button
                  className={`mr-2 py-2 px-4 font-w-[500] ${
                    jobDetailsView === "containerDetails"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => setJobDetailsView("containerDetails")}
                >
                  Containers
                </button>
                <button
                  className={`mr-2 py-2 px-4 font-w-[500] ${
                    jobDetailsView === "transporterDetails"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => setJobDetailsView("transporterDetails")}
                >
                  Transporters
                </button>
                <button
                  className={`py-2 px-4 font-w-[500] ${
                    jobDetailsView === "map"
                      ? "border-b border-blueText text-blueText"
                      : "text-notSelected"
                  }`}
                  onClick={() => setJobDetailsView("map")}
                >
                  Map
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <div
                className={
                  jobDetailsView === "jobDetails" ? "h-full" : "hidden"
                }
              >
                <JobDetails job={selectedJob} hideMap={true} />
              </div>
              <div
                className={
                  jobDetailsView === "containerDetails" ? "h-full" : "hidden"
                }
              >
                <ContainerDetails
                  job={selectedJob}
                  hideContainerAssignment={true}
                />
              </div>
              <div
                className={
                  jobDetailsView === "transporterDetails" ? "h-full" : "hidden"
                }
              >
                <TransporterDetails jobOrder={selectedJob} />
              </div>
              <div
                className={
                  jobDetailsView === "map" ? "h-full p-[20px]" : "hidden"
                }
              >
                <MapComponent job={selectedJob} />
              </div>
            </div>
          </div>
        </Modal>
      )}

      <UpdateContainerModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        containerData={editContainerData}
        onUpdateSuccess={async () => {
          handleCloseEditModal();
          await fetchActiveTrips(true, true);
        }}
      />

      <UpdateTruckModal
        isOpen={isTruckModalOpen}
        onClose={handleCloseTruckModal}
        truckData={editTruckData}
        onUpdateSuccess={async () => {
          handleCloseTruckModal();
          await fetchActiveTrips(true, true);
        }}
      />

      <ForceEndTripModal
        isOpen={isForceEndTripModalOpen}
        onClose={handleCloseForceEndTripModal}
        tripData={selectedTripToEnd}
        onEndTripSuccess={handleEndTripSuccess}
      />

      {transferTripData && (
        <TransferTripModal
          isOpen={isTransferTripModalOpen}
          onClose={() => setIsTransferTripModalOpen(false)}
          currentDriver={{
            id: transferTripData.driverId,
            name: transferTripData.driverName,
            truck: transferTripData.truck,
          }}
          tripId={transferTripData.tripId}
          transporterId={transferTripData.transporterId}
          transporterName={transferTripData.transporterName}
          onTransferSuccess={fetchActiveTrips}
        />
      )}
    </div>
  );
}

export default ActiveTrips;
